import { DragDropModule, CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialogModule, MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ButtonActionComponent } from "@component/button-action/button-action.component";
import { ButtonNewComponent } from "@component/button-new/button-new.component";
import { AppRolesDirective } from "@directives/appRoles/app-roles.directive";
import { DialogParams } from "@interfaces/dialogParam.interface.model";
import { AccesoRapidoDto, SugerenciaAccesoRapidoDto, ActualizarOrdenRequest } from "@models/accesoRapido/accesoRapidoDto.model";
import { AccionesDialog } from "@models/base/identificadores.model";
import { AccesoRapidoService } from "@services/accesosRapido/accesoRapido.service";
import { SnackBarService } from "@services/snackBar/snack-bar-service.service";
import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";
import { finalize, Subject, takeUntil, debounceTime, distinctUntilChanged } from "rxjs";
import { DialogFormAccesosRapidoComponent } from "../dialog-form-accesos-rapido/dialog-form-accesos-rapido.component";

@Component({
  selector: 'app-accesos-rapidos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    DragDropModule,
    MatToolbarModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatListModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatDividerModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatBadgeModule,
    NgxSpinnerModule,
    ButtonNewComponent,
    ButtonActionComponent,
    AppRolesDirective,
  ],
  templateUrl: './accesos-rapido.component.html',
  styleUrl: './accesos-rapido.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccesosRapidoComponent implements OnDestroy, OnInit {

  // Data
  accesos: AccesoRapidoDto[] = [];
  accesosFiltrados: AccesoRapidoDto[] = [];
  sugerencias: SugerenciaAccesoRapidoDto[] = [];
  gruposDisponibles: string[] = [];

  // State
  accionesDialog = AccionesDialog;
  sinAccesos: boolean = false;
  cargando: boolean = false;
  usuarioId = 1; // TODO: Obtener del contexto de usuario

  // 🆕 BANDERAS PARA CONTROLAR LA RENDERIZACIÓN
  componenteRenderizado: boolean = false;
  datosInicializados: boolean = false;

  // Form
  filtrosForm!: FormGroup;

  // Subscriptions
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
    private accesoRapidoService: AccesoRapidoService,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
    this.setupFormSubscriptions();
  }

  ngOnInit(): void {
    this.inicializarComponente();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 🆕 MÉTODO PRINCIPAL DE INICIALIZACIÓN
  private async inicializarComponente(): Promise<void> {
    try {
      // Reset states
      this.componenteRenderizado = false;
      this.datosInicializados = false;
      this.sinAccesos = false;
      this.cargando = true;
      this.spinner.show("spAccesoRapido");

      // Cargar datos en paralelo
      await Promise.all([
        this.cargarAccesos(),
        this.cargarSugerencias()
      ]);

      // Todo cargado exitosamente
      this.datosInicializados = true;
      this.componenteRenderizado = true;

    } catch (error) {
      console.error('Error al inicializar componente:', error);
      this.datosInicializados = true;
      this.componenteRenderizado = true;

    } finally {
      this.cargando = false;
      this.spinner.hide("spAccesoRapido");
      this.cdr.detectChanges();
    }
  }

  private initForm(): void {
    this.filtrosForm = this.formBuilder.group({
      filtroTexto: [''],
      grupoSeleccionado: [''],
      mostrarSoloFavoritos: [false]
    });
  }

  private setupFormSubscriptions(): void {
    // Filtro de texto con debounce
    this.filtrosForm.get('filtroTexto')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.aplicarFiltros();
      });

    // 🔧 FILTRO DE GRUPO - USAR EL VALOR ACTUAL DEL OBSERVABLE
    this.filtrosForm.get('grupoSeleccionado')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((nuevoGrupo) => {
        // Usar el valor del observable directamente
        this.aplicarFiltros(nuevoGrupo);
      });

    // Filtro de favoritos - inmediato
    this.filtrosForm.get('mostrarSoloFavoritos')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.aplicarFiltros();
      });
  }

  // 🔧 APLICAR FILTROS CORREGIDO
  private aplicarFiltros(grupoSeleccionado?: string): void {
    const formValues = this.filtrosForm.value;
    let filtrados = [...this.accesos]; // Crear copia desde el inicio

    // Filtro por texto
    if (formValues.filtroTexto?.trim()) {
      const texto = formValues.filtroTexto.toLowerCase().trim();
      filtrados = filtrados.filter(a =>
        a.titulo.toLowerCase().includes(texto) ||
        a.url.toLowerCase().includes(texto) ||
        (a.descripcion && a.descripcion.toLowerCase().includes(texto))
      );
    }

    // Filtro por favoritos
    if (formValues.mostrarSoloFavoritos) {
      filtrados = filtrados.filter(a => a.esFavorito);
    }

    // 🔧 FILTRO POR GRUPO - USAR PARÁMETRO O VALOR DEL FORM
    const grupoAFiltrar = grupoSeleccionado !== undefined ? grupoSeleccionado : formValues.grupoSeleccionado;
    if (grupoAFiltrar) {
      filtrados = filtrados.filter(a => a.grupo === grupoAFiltrar);
    }

    // Asignar nueva referencia para OnPush change detection
    this.accesosFiltrados = [...filtrados];
    this.cdr.detectChanges();
  }

  // Getters para acceso fácil desde el template
  get filtroTexto(): string {
    return this.filtrosForm.get('filtroTexto')?.value || '';
  }

  get grupoSeleccionado(): string {
    return this.filtrosForm.get('grupoSeleccionado')?.value || '';
  }

  get mostrarSoloFavoritos(): boolean {
    return this.filtrosForm.get('mostrarSoloFavoritos')?.value || false;
  }

  // 🔄 CARGAR ACCESOS COMO PROMESA
  cargarAccesos(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.accesoRapidoService.getByUser(this.usuarioId, false)
        .subscribe({
          next: (accesos) => {
            this.accesos = accesos.map(acceso => ({
              ...acceso,
              contadorVisitas: acceso.contadorVisitas || 0,
              ultimoAcceso: acceso.ultimoAcceso ?? undefined,
              esFavorito: acceso.esFavorito || false,
              activo: acceso.activo !== false
            })).sort((a, b) => {
              if (a.esFavorito && !b.esFavorito) return -1;
              if (!a.esFavorito && b.esFavorito) return 1;
              return a.orden - b.orden;
            });

            this.sinAccesos = this.accesos.length === 0;
            this.extraerGrupos();
            this.aplicarFiltros();
            resolve();
          },
          error: (error) => {
            console.error('Error al cargar accesos:', error);
            this.snackBar.showError('Error al cargar los accesos rápidos', 'Error');
            this.sinAccesos = true;
            this.accesosFiltrados = [];
            reject(error);
          }
        });
    });
  }

  // 🔄 CARGAR SUGERENCIAS COMO PROMESA
  cargarSugerencias(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.accesoRapidoService.getSugerencias(this.usuarioId, 5)
        .subscribe({
          next: (sugerencias) => {
            this.sugerencias = sugerencias;
            resolve();
          },
          error: (error) => {
            console.error('Error al cargar sugerencias:', error);
            // No rechazar la promesa por sugerencias, no es crítico
            resolve();
          }
        });
    });
  }

  // 🔄 RECARGAR SOLO ACCESOS (para usar después de CRUD)
  async recargarSoloAccesos(): Promise<void> {
    try {
      this.cargando = true;
      this.spinner.show("spAccesoRapido");
      await this.cargarAccesos();
    } catch (error) {
      console.error('Error al recargar accesos:', error);
    } finally {
      this.cargando = false;
      this.spinner.hide("spAccesoRapido");
      this.cdr.detectChanges();
    }
  }

  // 🔄 RECARGAR COMPONENTE COMPLETO
  recargarComponente(): void {
    this.inicializarComponente();
  }

  extraerGrupos(): void {
    const grupos = [...new Set(this.accesos.map(a => a.grupo).filter(g => g))];
    this.gruposDisponibles = grupos.sort();
  }

  toggleFavorito(acceso: AccesoRapidoDto): void {
    this.spinner.show("spAccesoRapido");
    this.accesoRapidoService.toggleFavorito(acceso.id, this.usuarioId)
      .subscribe({
        next: () => {
          // Encontrar el acceso en el array principal y actualizar
          const index = this.accesos.findIndex(a => a.id === acceso.id);
          if (index !== -1) {
            // Crear un nuevo objeto para forzar change detection
            this.accesos[index] = {
              ...this.accesos[index],
              esFavorito: !this.accesos[index].esFavorito
            };

            // Crear nuevas referencias de los arrays
            this.accesos = [...this.accesos];
            this.reorganizarAccesos();
            this.aplicarFiltros();
          }

          this.spinner.hide("spAccesoRapido");
          this.snackBar.showSuccess('Favorito actualizado correctamente', 'Éxito');
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.spinner.hide("spAccesoRapido");
          console.error('Error al actualizar favorito:', error);
          this.snackBar.showError('Error al actualizar favorito', 'Error');
        }
      });
  }

  // Método para toggle favoritos desde toolbar
  toggleMostrarSoloFavoritos(): void {
    const currentValue = this.filtrosForm.get('mostrarSoloFavoritos')?.value;
    this.filtrosForm.patchValue({ mostrarSoloFavoritos: !currentValue });
  }

  private reorganizarAccesos(): void {
    this.accesos.sort((a, b) => {
      if (a.esFavorito && !b.esFavorito) return -1;
      if (!a.esFavorito && b.esFavorito) return 1;
      return a.orden - b.orden;
    });
  }

  ordenar(event: CdkDragDrop<AccesoRapidoDto[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const accesos = [...this.accesosFiltrados];
    moveItemInArray(accesos, event.previousIndex, event.currentIndex);

    // Actualizar orden en el array principal
    accesos.forEach((acceso, index) => {
      const accesoOriginal = this.accesos.find(a => a.id === acceso.id);
      if (accesoOriginal) {
        accesoOriginal.orden = index + 1;
      }
    });

    // Crear request para actualizar orden en API
    const request: ActualizarOrdenRequest = {
      accesos: accesos.map(a => ({ id: a.id, orden: a.orden }))
    };

    this.spinner.show("spAccesoRapido");
    this.accesoRapidoService.actualizarOrden(request, this.usuarioId)
      .subscribe({
        next: () => {
          this.snackBar.showSuccess('Orden actualizado correctamente', 'Éxito');
          this.reorganizarAccesos();
          this.aplicarFiltros();
          this.spinner.hide("spAccesoRapido");
        },
        error: (error) => {
          console.error('Error al actualizar orden:', error);
          this.snackBar.showError('Error al actualizar el orden', 'Error');
          this.spinner.hide("spAccesoRapido");
          this.recargarSoloAccesos(); // Recargar para revertir cambios
        }
      });
  }

  registrarVisita(acceso: AccesoRapidoDto): void {
    this.accesoRapidoService.registrarVisita(acceso.id, this.usuarioId)
      .subscribe({
        next: (data) => {
          // Encontrar el acceso en el array principal y actualizar
          const index = this.accesos.findIndex(a => a.id === acceso.id);
          if (index !== -1) {
            // Crear un nuevo objeto para forzar change detection
            this.accesos[index] = {
              ...this.accesos[index],
              contadorVisitas: (this.accesos[index].contadorVisitas || 0) + 1,
              ultimoAcceso: new Date()
            };

            // Crear nuevas referencias de los arrays para OnPush
            this.accesos = [...this.accesos];
            this.aplicarFiltros();
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error al registrar visita:', error);
          // No mostrar error al usuario ya que no afecta la navegación
        }
      });
  }

  agregarDesdeSugerencia(sugerencia: SugerenciaAccesoRapidoDto): void {
    const dialogData: DialogParams<{ titulo: string; url: string; descripcion: string; }> = {
      accion: AccionesDialog.Agregar,
      entidad: {
        titulo: this.extraerNombreDeSugerencia(sugerencia.url),
        url: sugerencia.url,
        descripcion: sugerencia.motivo || ''
      },
      modulo: 'AccesosRapidos'
    };

    this.openDialog(dialogData);
  }

  private extraerNombreDeSugerencia(url: string): string {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;
      const domain = hostname.replace(/^www\./, '');
      return domain.charAt(0).toUpperCase() + domain.slice(1);
    } catch {
      return url;
    }
  }

  limpiarFiltros(): void {
    this.filtrosForm.reset({
      filtroTexto: '',
      grupoSeleccionado: '',
      mostrarSoloFavoritos: false
    });
  }

  verificarUrlsMasivas(): void {
    this.spinner.show("spAccesoRapido");

    this.accesoRapidoService.verificarUrlsMasivas(this.usuarioId, 30)
      .pipe(finalize(() => {
        this.spinner.hide("spAccesoRapido");
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (resultado) => {
          const mensaje = `Verificación completada: ${resultado.exitosas} exitosas, ${resultado.fallidas} fallidas, ${resultado.inaccesibles} inaccesibles`;
          this.snackBar.showSuccess(mensaje, 'Verificación Masiva');
          this.recargarSoloAccesos();
        },
        error: (error: any) => {
          console.error('Error en verificación masiva:', error);
          this.snackBar.showError('Error en la verificación masiva', 'Error');
        }
      });
  }

  reorganizarAutomaticamente(): void {
    this.spinner.show("spAccesoRapido");
    this.accesoRapidoService.reorganizar(this.usuarioId, 'uso', true)
      .subscribe({
        next: () => {
          this.snackBar.showSuccess('Accesos reorganizados por uso', 'Éxito');
          this.recargarSoloAccesos();
          this.spinner.hide("spAccesoRapido");
        },
        error: (error) => {
          console.error('Error al reorganizar:', error);
          this.spinner.hide("spAccesoRapido");
          this.snackBar.showError('Error al reorganizar accesos', 'Error');
        }
      });
  }

  // Funciones de tracking para optimizar el rendimiento
  trackByAcceso(index: number, acceso: AccesoRapidoDto): number {
    return acceso.id;
  }

  trackBySugerencia(index: number, sugerencia: SugerenciaAccesoRapidoDto): number {
    return sugerencia.id;
  }

  openDialog<T>(params: DialogParams<T>) {
    const { accion, entidad } = params;

    const data = {
      ...entidad,
      accion,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.data = data;
    dialogConfig.maxHeight = '90vh';

    switch (accion) {
      case AccionesDialog.Ver:
        dialogConfig.width = "80%";
        dialogConfig.maxWidth = "1000px";
        break;
      default:
        dialogConfig.width = "70%";
        dialogConfig.maxWidth = "800px";
        break;
    }

    const dialogRef = this.dialog.open(DialogFormAccesosRapidoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result || !result.event) return;

      switch (result.event) {
        case AccionesDialog.Agregar:
        case AccionesDialog.Modificar:
        case AccionesDialog.Eliminar:
          this.recargarSoloAccesos(); // Usar método optimizado
          break;
        default:
          break;
      }
    });
  }
}
